import { scaleOrdinal } from 'd3-scale';
import { line as shapeLine } from 'd3-shape';
import { quadtree } from 'd3-quadtree';
import * as d3 from 'd3-selection';
import { extent as arrayExtent } from 'd3-array';

import colorScheme from './colorScheme';
import findPoints from './findPoints';
import getCurveType from './getCurveType';
import getScaleType from './getScaleType';
import minBy from './minBy';

function renderSparkline() {
  if (this.props.series) {
    const { series = { data: [] } } = this.props;
    const marginProps = Object.assign({
      top: 10,
      right: 40,
      bottom: 10,
      left: 40,
    }, series.margin);
    const pointProps = Object.assign({
      color: null,
      radius: 4,
    }, series.points);
    // Persist the color scale
    const scheme = series.colors instanceof Array
      ? series.colors
      : colorScheme;
    const colorScale = this.colorScale
      || (this.colorScale = scaleOrdinal().range(scheme));

    // Initialize accessors for various properties.
    const xAccessor = series.xAccessor
      || ((d) => d.key);
    const yAccessor = series.yAccessor
      || ((d) => d.value);
    const colorAccessor = series.colorAccessor
      || ((d, i) => colorScale(i));

    // Create the SVG.
    let svg = d3.select(this.chartRef.current)
      .data([0]);
    svg = svg.enter()
      .append('svg')
      .attr('class', 'sparkline-svg')
      .merge(svg);
    // Get the container dimensions.
    const width = svg.node().parentNode.clientWidth;
    const height = svg.node().parentNode.clientHeight;
    const chartWidth = width - marginProps.left - marginProps.right;
    const chartHeight = height - marginProps.top - marginProps.bottom;
    if (chartWidth <= 0 || chartHeight <= 0) {
      return;
    }
    svg
      .attr('width', width)
      .attr('height', height);

    // Add an invisible rectangle to the SVG to catch mouse events.
    const capture = svg.selectAll('.capture')
      .data([0]);
    capture.enter()
      .append('rect')
      .attr('class', 'capture')
      .style('opacity', 0.001)
      .merge(capture)
      .attr('x', 0)
      .attr('width', width)
      .attr('y', 0)
      .attr('height', height);

    // Set the chart margin.
    let margin = svg.selectAll('.margin')
      .data([0]);
    margin = margin.enter()
      .append('g')
      .attr('class', 'margin')
      .merge(margin)
      .attr('transform', `translate(${marginProps.left}, ${marginProps.top})`);

    // Examine the data for domains.
    const xDomain = series.xDomain
      || arrayExtent(series.data, xAccessor);
    const yDomain = series.yDomain
      || arrayExtent(series.data, yAccessor);

    // Include the baseline with the domain.
    if (Number.isFinite(series.baseline)) {
      yDomain[0] = Math.min(yDomain[0], series.baseline);
      yDomain[1] = Math.max(yDomain[1], series.baseline);
    }

    // Create the scales.
    const xScale = (getScaleType('linear'))()
      .domain(xDomain)
      .range([0, chartWidth]);
    const yScale = (getScaleType('linear'))()
      .domain(yDomain)
      .range([chartHeight, 0])
      .nice();

    // Draw the baseline.
    const baseline = margin
      .selectAll('.baseline')
      .data(Number.isFinite(series.baseline)
        ? [series.baseline]
        : []);
    baseline.exit().remove();
    baseline.enter()
      .append('line')
      .attr('class', 'baseline')
      .merge(baseline)
      .attr('x1', xScale.range()[0])
      .attr('x2', xScale.range()[1])
      .attr('y1', yScale)
      .attr('y2', yScale)
      .lower();

    // Draw the sparkline.
    const getX = (d, i) => xScale(xAccessor(d, i));
    const getY = (d, i) => yScale(yAccessor(d, i));
    // const showPoint = d => d.hover || (!currPoint && d.mark);
    const showPoint = (d) => d.hover || d.mark;
    const generator = shapeLine()
      .x(getX)
      .y(getY)
      .defined((d) => Number.isFinite(yAccessor(d)))
      .curve(getCurveType(series.curve));

    const line = margin
      .selectAll('.series')
      .data([series.data]);
    line.exit().remove();
    line.enter()
      .append('path')
      .attr('class', 'series')
      .merge(line)
      .attr('d', generator)
      .style('stroke', colorAccessor)
      .style('stroke-width', series.strokeWidth)
      .style('fill', 'none');

    // Draw the points.
    const drawPoints = () => {
      const point = margin
        .selectAll('.point')
        .data(series.data.filter(showPoint));
      point.exit().remove();
      point.enter()
        .append('circle')
        .attr('class', 'point')
        .merge(point)
        .attr('cx', getX)
        .attr('cy', getY)
        .attr('r', pointProps.radius)
        .style('fill', pointProps.color)
        .classed('mark', (d) => d.mark)
        .classed('hover', (d) => d.hover);
    };
    drawPoints();

    // Set up mouse detection using a quadtree.
    const quad = quadtree()
      .x(getX)
      .y(getY)
      .addAll(series.data);
    svg.on('mousemove', () => {
      // Find the nearest point that intersects the cursor's x position.
      const [x] = d3.mouse(margin.node());
      const found = findPoints(quad, x, null, Math.max(8, pointProps.radius));
      const point = minBy(found, (p) => Math.abs(x - getX(p)));
      if (this.currPoint !== point) {
        if (this.currPoint) {
          this.currPoint.hover = false;
          this.dispatcher.call('pointleave', this, this.currPoint);
        }
        if (point) {
          point.hover = true;
          this.dispatcher.call('pointenter', this, point);
        }
        this.currPoint = point;
        drawPoints();
      }
    });
    svg.on('mouseleave', () => {
      if (this.currPoint) {
        this.currPoint.hover = false;
        this.dispatcher.call('pointleave', this, this.currPoint);
        this.currPoint = null;
        drawPoints();
      }
    });

  }
}

export default renderSparkline;
